import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet, View, useWindowDimensions } from "react-native";
import { DataTable } from "react-native-paper";
import { useTranslation } from "react-i18next";
import {
  Field,
  OrderByItem,
  TableResponsiveConfig,
} from "../config/companies/config";
import { SMALL_SCREEN_WIDTH } from "../constants/constants";
import { compareTableValues } from "../utils/tableSort";
import { getNestedValue } from "../utils/utils";
import TableClassicLayout from "./tableView/TableClassicLayout";
import TableTransposedLayout from "./tableView/TableTransposedLayout";

interface CollectionType {
  uniqIdNumber?: boolean;
  [key: string]: any;
}

interface DataItem {
  id: string | number;
  [key: string]: any;
}

interface DataTableViewProps {
  fields: Field[];
  data: DataItem[];
  collection: CollectionType;
  orderBy?: OrderByItem[];
  tableResponsive?: TableResponsiveConfig;
  permittedFields: Field[];
  getRowStyle: (item: DataItem, fields: Field[]) => any;
  navigateToDetailsScreen: (
    fields: Field[],
    data: DataItem,
    collection: CollectionType,
  ) => void;
  onLoadMore?: (increment: number) => void;
  onLoadAll?: () => void;
  hasLimit?: boolean;
  totalCount?: number | null;
}

const DataTableView: React.FC<DataTableViewProps> = ({
  fields,
  data,
  collection,
  orderBy,
  tableResponsive: {
    enabled: responsiveEnabled = false,
    breakpoint: responsiveBreakpoint = SMALL_SCREEN_WIDTH,
  } = {},
  permittedFields,
  getRowStyle,
  navigateToDetailsScreen,
  onLoadMore,
  onLoadAll,
  hasLimit = false,
  totalCount = null,
}) => {
  const { t } = useTranslation();
  const { width: viewportWidth } = useWindowDimensions();
  const fieldsByName = useMemo<Record<string, Field>>(
    () =>
      fields.reduce(
        (acc, field) => {
          acc[field.name] = field;
          return acc;
        },
        {} as Record<string, Field>,
      ),
    [fields],
  );

  const [page, setPage] = useState<number>(0);
  const itemsPerPage = 20;

  const [activeSort, setActiveSort] = useState<{
    field: string;
    direction: "ascending" | "descending";
  } | null>(() => {
    if (!orderBy || orderBy.length === 0) return null;
    const first = orderBy[0];
    return {
      field: first.field,
      direction: first.direction === "desc" ? "descending" : "ascending",
    };
  });

  const sortOptions = useMemo<
    Record<string, "ascending" | "descending">
  >(() => {
    if (!activeSort) return {};
    return { [activeSort.field]: activeSort.direction };
  }, [activeSort]);

  const handlePageChange = (newPage: number, totalPages: number) => {
    const currentDataCount = data.length;
    const neededDataCount = (newPage + 1) * itemsPerPage;

    if (
      onLoadMore &&
      hasLimit &&
      newPage > page &&
      neededDataCount > currentDataCount
    ) {
      // Load enough to cover the next page (increment by 20)
      onLoadMore(itemsPerPage);
    }

    // If clicking last page button, load all documents
    if (onLoadAll && hasLimit && newPage === totalPages - 1) {
      onLoadAll();
    }

    setPage(newPage);
  };

  const handleSort = (fieldName: string) => {
    setActiveSort((prev) => {
      if (!prev || prev.field !== fieldName) {
        return { field: fieldName, direction: "ascending" };
      }
      return {
        field: fieldName,
        direction: prev.direction === "ascending" ? "descending" : "ascending",
      };
    });
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    if (!activeSort) return data;

    const sortField = activeSort.field;
    const direction = activeSort.direction;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sortField);
      const bValue = getNestedValue(b, sortField);

      if (aValue == null && bValue != null) return 1;
      if (aValue != null && bValue == null) return -1;
      if (aValue == null && bValue == null) return 0;

      const comparison = compareTableValues(
        aValue,
        bValue,
        fieldsByName[sortField],
      );

      return direction === "ascending" ? comparison : -comparison;
    });
  }, [data, orderBy, activeSort, fieldsByName]);

  const finalTotal = totalCount !== null ? totalCount : sortedData.length;
  const totalPages = Math.ceil(finalTotal / itemsPerPage);
  const displayPage = totalPages > 0 ? Math.min(page, totalPages - 1) : 0;

  // Calculate pagination
  const from = displayPage * itemsPerPage;
  const to = Math.min((displayPage + 1) * itemsPerPage, sortedData.length);
  const paginatedData = sortedData.slice(from, to);

  useEffect(() => {
    if (totalPages === 0 && page !== 0) {
      setPage(0);
      return;
    }

    if (totalPages > 0 && page >= totalPages) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  const shouldUseTransposedTable =
    responsiveEnabled && viewportWidth <= responsiveBreakpoint;

  return (
    <View style={styles.container}>
      {shouldUseTransposedTable ? (
        <TableTransposedLayout
          orderBy={orderBy}
          sortOptions={sortOptions}
          handleSort={handleSort}
          paginatedData={paginatedData}
          fields={fields}
          collection={collection}
          permittedFields={permittedFields}
          getRowStyle={getRowStyle}
          navigateToDetailsScreen={navigateToDetailsScreen}
          t={t}
        />
      ) : (
        <TableClassicLayout
          permittedFields={permittedFields}
          orderBy={orderBy}
          sortOptions={sortOptions}
          handleSort={handleSort}
          paginatedData={paginatedData}
          fields={fields}
          collection={collection}
          getRowStyle={getRowStyle}
          navigateToDetailsScreen={navigateToDetailsScreen}
          t={t}
        />
      )}

      {sortedData.length >= itemsPerPage && (
        <DataTable.Pagination
          page={displayPage}
          numberOfPages={totalPages}
          onPageChange={(newPage) => handlePageChange(newPage, totalPages)}
          label={`${from + 1}-${to} of ${finalTotal}`}
          showFastPaginationControls
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default DataTableView;
